"use client";

// Barra de navegación de la app, para quien ya inició sesión.
//
// Por qué existe: la barra inferior es `sm:hidden`, o sea SOLO teléfono, y cada
// pantalla traía su propio encabezado con dos o tres enlaces sueltos. Resultado:
// en escritorio, alguien con sesión iniciada quedaba encerrado — desde /local no
// había forma de llegar a la tienda, desde /tecnico tampoco, y la portada seguía
// ofreciendo "Ingresar" y "Crear cuenta" a alguien que ya estaba adentro.
//
// Los destinos dependen del rol, que es lo que hace que sirva: un local y un
// cliente no tienen por qué ver los mismos lugares.
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import Avatar from "./Avatar";
import Icon, { IconName } from "./Icon";
import { INICIO_KEY, api, clearTokens, hasSession } from "../lib/api";

interface Yo {
  id: string;
  fullName: string;
  roles: { role: string }[];
  kycLevel: number;
}

interface Destino {
  href: string;
  texto: string;
  icono: IconName;
}

// El panel de administración tiene su propia navegación completa, y las
// pantallas de acceso no son parte de la app: encimarles otra barra confunde.
// /buscar es inmersivo como Uber: el mapa ocupa todo y se sale con su flecha
const OCULTA_EN = ["/admin", "/ingresar", "/registro", "/recuperar", "/accesos", "/auth", "/buscar"];

/** El inicio de cada rol: el local vive en su bandeja, no en la portada. */
export function inicioDe(roles: string[]): string {
  if (roles.includes("ADMIN")) return "/admin";
  if (roles.includes("BUSINESS")) return "/local";
  if (roles.includes("TECH")) return "/tecnico";
  return "/buscar";
}

function destinosDe(roles: string[]): Destino[] {
  // "Inicio" lleva al inicio DEL ROL, no a la landing: quien ya entró no
  // necesita que le vendan el producto de nuevo.
  const d: Destino[] = [{ href: inicioDe(roles), texto: "Inicio", icono: "home" }];
  if (roles.includes("BUSINESS")) {
    d.push({ href: "/local/estadisticas", texto: "Estadísticas", icono: "chart" });
  }
  if (roles.includes("TECH") && roles.includes("BUSINESS")) {
    d.push({ href: "/tecnico", texto: "Mis trabajos", icono: "wrench" });
  }
  // El mapa es para TODOS: el técnico también mira su zona, compara precios y
  // recomienda a un colega. Esconderlo por rol fue un error que el dueño marcó
  // probando ("ya no tengo dónde la búsqueda real"). Para el cliente, "Inicio"
  // ya es el mapa y el dedupe de abajo evita repetirlo.
  d.push({ href: "/buscar", texto: "Mapa de técnicos", icono: "map-pin" });
  d.push(
    { href: "/solicitudes", texto: "Reparaciones", icono: "clock" },
    { href: "/tienda", texto: "Tienda", icono: "cart" },
    { href: "/beneficios", texto: "Beneficios", icono: "sparkles" },
  );
  // Sin duplicados por destino: para el cliente, "Inicio" YA es /buscar, y
  // repetirlo como "Buscar técnico" ponía dos hijos con la misma key en React
  // (y dos entradas iguales en el menú, que es lo que de verdad confunde).
  // Gana la primera aparición: "Inicio" pisa a la variante con otro nombre.
  const vistos = new Set<string>();
  return d.filter((x) => !vistos.has(x.href) && (vistos.add(x.href), true));
}

export default function NavSuperior() {
  const ruta = usePathname();
  const router = useRouter();
  const [yo, setYo] = useState<Yo | null>(null);
  const [sinSesion, setSinSesion] = useState(true);
  const [menu, setMenu] = useState(false);
  const cajaMenu = useRef<HTMLDivElement>(null);

  const cargar = useCallback(() => {
    if (!hasSession()) { setSinSesion(true); setYo(null); return; }
    setSinSesion(false);
    // Si falla, la barra se muestra igual con los destinos básicos: quedarse sin
    // navegación por un bache de señal es peor que mostrarla incompleta.
    api<Yo>("/auth/me")
      .then((y) => {
        setYo(y);
        // El cache que hace instantáneo el "Inicio": lo leen el logo, la barra
        // de abajo y la redirección de la portada, sin esperar a este viaje.
        localStorage.setItem(INICIO_KEY, inicioDe(y.roles.map((r) => r.role)));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => { cargar(); }, [cargar, ruta]);
  useEffect(() => { setMenu(false); }, [ruta]);

  // Cerrar el menú al tocar afuera o con Escape
  useEffect(() => {
    if (!menu) return;
    const afuera = (e: MouseEvent) => {
      if (cajaMenu.current && !cajaMenu.current.contains(e.target as Node)) setMenu(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("mousedown", afuera);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", afuera);
      document.removeEventListener("keydown", esc);
    };
  }, [menu]);

  if (sinSesion) return null;
  if (OCULTA_EN.some((p) => ruta === p || ruta.startsWith(`${p}/`))) return null;

  const roles = yo?.roles.map((r) => r.role) ?? [];
  const destinos = destinosDe(roles);
  const esAdmin = roles.includes("ADMIN");
  const activo = (href: string) => (href === "/" ? ruta === "/" : ruta.startsWith(href));

  function salir() {
    clearTokens();
    router.push("/");
    router.refresh();
  }

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#14191B]/92 backdrop-blur">
      <div className="max-w-6xl mx-auto px-4 sm:px-5 h-14 flex items-center gap-2">
        <Link href={yo ? inicioDe(roles) : "/"} className="flex items-center gap-2 font-bold text-[17px] tracking-tight shrink-0">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icon.svg" alt="" width={28} height={28} className="rounded-lg" />
          <span className="hidden xs:inline sm:inline">busco</span>
        </Link>

        {/* Escritorio: los destinos a la vista. Es justamente lo que faltaba. */}
        <nav className="hidden md:flex items-center gap-0.5 ml-3 min-w-0">
          {destinos.map((d) => (
            <Link
              key={d.href}
              href={d.href}
              aria-current={activo(d.href) ? "page" : undefined}
              className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13.5px] font-semibold whitespace-nowrap transition ${
                activo(d.href)
                  ? "bg-white/8 text-[#E8A23C]"
                  : "text-[#B5C0BB] hover:bg-white/5 hover:text-[#EDF1EF]"
              }`}
            >
              <Icon name={d.icono} size={15} /> {d.texto}
            </Link>
          ))}
        </nav>

        <div className="flex-1" />

        {esAdmin && (
          <Link
            href="/admin"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-[#DE705C]/40 bg-[#DE705C]/10 px-3 py-1.5 text-[12.5px] font-bold text-[#DE705C] hover:bg-[#DE705C]/18"
          >
            <Icon name="shield" size={14} /> Panel
          </Link>
        )}

        {/* Menú de la cuenta. En teléfono también lleva los destinos, porque la
            barra de abajo solo tiene cinco y no alcanza para todos los roles. */}
        <div className="relative" ref={cajaMenu}>
          <button
            onClick={() => setMenu((v) => !v)}
            aria-expanded={menu}
            aria-haspopup="menu"
            className="inline-flex items-center gap-2 min-h-[44px] pl-2 pr-2.5 rounded-lg hover:bg-white/5"
          >
            <Avatar userId={yo?.id} nombre={yo?.fullName ?? "?"} size={32} className="!rounded-full" />
            <span className="hidden sm:inline text-[13.5px] font-semibold text-[#B5C0BB] max-w-[10rem] truncate">
              {yo?.fullName ?? "Mi cuenta"}
            </span>
            <Icon name="chevron-down" size={15} className="text-[#808D88]" />
          </button>

          {menu && (
            <div
              role="menu"
              className="absolute right-0 mt-1.5 w-60 rounded-xl border border-white/12 bg-[#1A2123] shadow-2xl overflow-hidden py-1.5"
            >
              {yo && (
                <div className="px-3.5 py-2 border-b border-white/8">
                  <div className="font-semibold text-[14px] truncate">{yo.fullName}</div>
                  <div className="text-[12px] text-[#808D88]">
                    {roles.map((r) => ETIQUETA_ROL[r] ?? r).join(" · ") || "Cliente"}
                  </div>
                </div>
              )}

              {/* En teléfono el menú es la navegación completa */}
              <div className="md:hidden py-1 border-b border-white/8">
                {destinos.map((d) => (
                  <Link
                    key={d.href}
                    href={d.href}
                    role="menuitem"
                    className={`flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] ${
                      activo(d.href) ? "text-[#E8A23C]" : "text-[#B5C0BB] hover:bg-white/5"
                    }`}
                  >
                    <Icon name={d.icono} size={16} /> {d.texto}
                  </Link>
                ))}
              </div>

              <Link href="/perfil" role="menuitem"
                className="flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-[#B5C0BB] hover:bg-white/5">
                <Icon name="user" size={16} /> Mi perfil
              </Link>
              {roles.includes("TECH") && (
                <Link href="/tecnico/perfil" role="menuitem"
                  className="flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-[#B5C0BB] hover:bg-white/5">
                  <Icon name="wrench" size={16} /> Mi perfil público
                </Link>
              )}
              <Link href="/plan" role="menuitem"
                className="flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-[#B5C0BB] hover:bg-white/5">
                <Icon name="card" size={16} /> Mi plan
              </Link>
              {esAdmin && (
                <Link href="/admin" role="menuitem"
                  className="sm:hidden flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-[#DE705C] hover:bg-white/5">
                  <Icon name="shield" size={16} /> Panel de administración
                </Link>
              )}
              <button onClick={salir} role="menuitem"
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] text-[#B5C0BB] hover:bg-white/5 border-t border-white/8 mt-1">
                <Icon name="logout" size={16} /> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

const ETIQUETA_ROL: Record<string, string> = {
  ADMIN: "Administrador",
  BUSINESS: "Local",
  TECH: "Técnico",
  CLIENT: "Cliente",
};
